import {
  Typography,
  Col,
  Row,
  Select,
  Space,
  Table,
  Modal,
  Spin,
  Image,
  Input,
  Carousel,
  Button,
  message,
} from 'antd'
import React, { useState, useEffect } from 'react'
import { DndProvider } from 'react-dnd'
import {
  STATESOPTIONS,
  DELAY_STATUS_OPTIONS,
  REDEEM_USER_VALIDATION_STATES,
} from 'src/constants'
import { HTML5Backend } from 'react-dnd-html5-backend'
import usePage from 'src/hooks/usePage'
import { searchRedeemUserReport, fetchMarketPlaces } from 'src/api/reports'
import { getMarketplaceByIds } from 'src/api/marketplace'
import {
  saveState,
  getDelayStatus,
  saveStatus,
  saveValidationStatus,
  saveBulkEdit,
} from 'src/api/redeemusers'
import { fetchUserInfo } from 'src/api/authen'
import RedeemUserSearch from './redeemUserSearch'
import { EyeOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { manualFetch } from 'src/utils/fetch'
import './RedeemUser.css'

const { Column } = Table

const RedeemUsers: React.FC = () => {
  const [redeemUser, setRedeemUser] = useState(false)
  const [products, setProducts] = useState<any[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [marketplaceDetails, setMarketplaceDetails] = useState<any>(null)
  const [carouselImages, setCarouselImages] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [sortBy, setSortBy] = useState<string | undefined>()
  const [sortOrder, setSortOrder] = useState<string | undefined>()
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [pendingStateChange, setPendingStateChange] = useState<{
    value: string
    record: any
  } | null>(null)

  const { pageData, onQuery, appendSearchForm, submitSearchForm } = usePage({
    getData: searchRedeemUserReport,
    searchData: searchRedeemUserReport,
    searchType: 'POST',
  })
  const [emailConfirmVisible, setEmailConfirmVisible] = useState(false)
  const [pendingEmailStateChange, setPendingEmailStateChange] = useState<{
    record: any
    value: string
  } | null>(null)

  const [delayStatuses, setDelayStatuses] = useState<string[]>([])
  const [delayModalVisible, setDelayModalVisible] = useState(false)
  const [selectedDelay, setSelectedDelay] = useState<{
    record: any
    status: string
  } | null>(null)
  const [delayReason, setDelayReason] = useState('')

  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([])
  const [bulkTrackingStatus, setBulkTrackingStatus] = useState<string | null>(
    null
  )
  const [bulkEmailConfirmVisible, setBulkEmailConfirmVisible] = useState(false)
  const [pendingBulkEmailAction, setPendingBulkEmailAction] = useState<{
    ids: any[]
    status: string
  } | null>(null)

  const fetchUserAccess = async () => {
    try {
      const { data } = await fetchUserInfo()
      setRedeemUser(
        data != null &&
          (data.permissions.includes('superadmin') ||
            data?.accessToUserInfo === true)
      )
    } catch (error) {
      console.error('Error fetching user access data:', error)
    }
  }

  const fetchImage = async (url: string) => {
    const response = await manualFetch(url, { headers: { accept: 'image/*' } })
    const imageBlob = await response.blob()
    return URL.createObjectURL(imageBlob)
  }

  const handleMarketplaceClick = async (marketPlaceName: string) => {
    try {
      const product = products.find((p) => p.name === marketPlaceName)
      if (!product) {
        console.warn('Marketplace not found:', marketPlaceName)
        return
      }
      setLoading(true)
      setIsModalVisible(true)
      const marketResponse = await getMarketplaceByIds([product.id])
      const item = marketResponse?.data?.[0]
      if (item) {
        const downloadedImages = await Promise.all(
          (item.images || []).map(
            async (imgUrl: string) => await fetchImage(imgUrl)
          )
        )
        setCarouselImages(downloadedImages)
        setMarketplaceDetails(item)
      } else {
        setMarketplaceDetails(null)
      }

      setLoading(false)
    } catch (error) {
      console.error('Error fetching marketplace details:', error)
      setLoading(false)
    }
  }

  const applyStateChange = async (
    value: string,
    record: any,
    sendEmail = false
  ) => {
    setLoading(true)
    try {
      await saveState(record, value, sendEmail)
      const updated = (pageData.data || []).map((item: any) =>
        item.id === record.id ? { ...item, state: value } : item
      )
      pageData.data = updated
      message.success('Updated successfully')
    } catch (error) {
      console.error('Error updating state:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleValidationStateChange = async (value: string, record: any) => {
    setLoading(true)
    await saveValidationStatus(record, value)
    const updated = (pageData.data || []).map((item: any) =>
      item.id === record.id ? { ...item, userValidationStatus: value } : item
    )
    pageData.data = updated
    message.success('Updated successfully')
    setLoading(false)
  }

  const handleBulkTrackingUpdate = () => {
    if (!bulkTrackingStatus || selectedRowKeys.length === 0) {
      message.warning('Select rows and status')
      return
    }

    const needsEmail = bulkTrackingStatus === 'DELIVERED'

    if (needsEmail) {
      setPendingBulkEmailAction({
        ids: selectedRowKeys,
        status: bulkTrackingStatus,
      })
      setBulkEmailConfirmVisible(true)
      return
    }

    executeBulkUpdate(false)
  }

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms))

  const executeBulkUpdate = async (sendEmail: boolean) => {
    if (!bulkTrackingStatus || selectedRowKeys.length === 0) return

    pageData.loading = true
    try {
      await saveBulkEdit(selectedRowKeys, bulkTrackingStatus, sendEmail)
      await delay(2000)
      onQuery({ ...pageData.query, refresh: Date.now() })

      setSelectedRowKeys([])
      setBulkTrackingStatus(null)
      message.success('Bulk update successful')
    } catch (e) {
      console.error(e)
      message.error('Bulk update failed')
    } finally {
      pageData.loading = false
    }
  }

  const handleStateChange = (value: string, record: any) => {
    if (value === 'CANCELED') {
      setPendingStateChange({ value, record })
      setConfirmVisible(true)
      return
    }

    if (value === 'DELIVERED' && record.marketPlaceCategory === 'GIFT_CARDS') {
      setPendingEmailStateChange({ record, value })
      setEmailConfirmVisible(true)
      return
    }

    applyStateChange(value, record)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
    setMarketplaceDetails(null)
    setCarouselImages([])
  }
  const loadDelayStatuses = async () => {
    const data = await getDelayStatus()
    setDelayStatuses(data || [])
  }

  const handleDelayStatusChange = (value: string, record: any) => {
    if (value === 'NO_DELAY') {
      return
    }
    setSelectedDelay({ record, status: value })
    const currentPage = parseInt(pageData.query.page) || 0
    setTimeout(() => onQuery({ page: currentPage }), 1000)
    setDelayReason('')
    setDelayModalVisible(true)
  }

  const handleSaveDelayStatus = async () => {
    if (!delayReason.trim()) {
      message.error('Reason is required')
      return
    }
    if (!selectedDelay) return
    setLoading(true)
    try {
      await saveStatus(selectedDelay.record, selectedDelay.status, delayReason)

      const currentPage = parseInt(pageData.query.page) || 0
      onQuery({ page: currentPage === 0 ? 1 : 0 })
      setTimeout(() => onQuery({ page: currentPage }), 100)

      setDelayModalVisible(false)
      setSelectedDelay(null)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const formatCategory = (category: string) => {
    if (!category) return ''
    return category
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getDelayStatusClass = (status?: string) => {
    switch (status) {
      case 'UPDATE':
        return 'delay-update'
      case 'APOLOGY':
        return 'delay-apology'
      case 'POINTS':
        return 'delay-points'
      default:
        return 'delay-none'
    }
  }

  const fetchProducts = async () => {
    try {
      const response = await fetchMarketPlaces()
      setProducts(response)
    } catch (error) {
      console.error('Error fetching products:', error)
    }
  }

  const normalizeDelayStatus = (status?: string | null) => {
    return status ?? 'NO_DELAY'
  }

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const newPage = pagination.current - 1
    const newPageSize = pagination.pageSize

    let newSortBy: string | undefined
    let newSortOrder: string | undefined
    if (sorter.field && sorter.order) {
      newSortBy = sorter.field
      newSortOrder = sorter.order === 'ascend' ? 'asc' : 'desc'
    } else {
      newSortBy = undefined
      newSortOrder = undefined
    }
    const sortChanged = newSortBy !== sortBy || newSortOrder !== sortOrder
    const page = sortChanged ? 0 : newPage

    const queryParams: any = {
      page: page.toString(),
      size: newPageSize.toString(),
    }
    if (newSortBy) {
      queryParams.sortBy = newSortBy
      queryParams.sortOrder = newSortOrder
    }

    setSortBy(newSortBy)
    setSortOrder(newSortOrder)
    onQuery(queryParams)
  }

  useEffect(() => {
    const total = pageData.total || 0
    const pageSize = parseInt(pageData.query.size) || 10
    const currentPage = parseInt(pageData.query.page) || 0
    const lastPage = Math.ceil(total / pageSize) - 1

    if (!pageData.loading && total > 0 && currentPage > lastPage) {
      onQuery({ page: lastPage.toString(), size: pageSize.toString() })
    }
  }, [
    pageData.loading,
    pageData.total,
    pageData.query.page,
    pageData.query.size,
  ])

  useEffect(() => {
    fetchProducts()
    fetchUserAccess()
    loadDelayStatuses()
  }, [])

  return (
    <>
      <RedeemUserSearch
        appendSearchForm={appendSearchForm}
        submitSearchForm={submitSearchForm}
      />

      <Row justify="space-between" style={{ marginBottom: 10 }}>
        <Col>
          <Space>
            <Select
              placeholder="Bulk Tracking Status"
              value={bulkTrackingStatus}
              onChange={setBulkTrackingStatus}
            >
              {STATESOPTIONS.filter((opt) => opt.value !== '').map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>

            {selectedRowKeys.length > 0 && bulkTrackingStatus && (
              <Button
                type="primary"
                onClick={handleBulkTrackingUpdate}
                className="btn-primary"
              >
                Apply Bulk Update
              </Button>
            )}
          </Space>
        </Col>
        <Col>
          <Select
            placeholder="Export"
            style={{ width: 160 }}
            onSelect={(value) => {
              if (value === 'pdf' || value === 'csv') {
                const action = value === 'pdf' ? 'Generate Pdf' : 'Generate Csv'
                appendSearchForm({ buttonAction: action })
                submitSearchForm()
                setTimeout(() => appendSearchForm({ buttonAction: '' }), 0)
              }
            }}
            allowClear
          >
            <Select.Option value="pdf">Export as PDF</Select.Option>
            <Select.Option value="csv">Export as CSV</Select.Option>
          </Select>
        </Col>
      </Row>

      <DndProvider backend={HTML5Backend}>
        <Table
          rowKey="id"
          dataSource={pageData.data}
          loading={pageData.loading}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            preserveSelectedRowKeys: true,
          }}
          rowClassName={(record: any) =>
            selectedRowKeys.includes(record.id) ? 'bulk-selected-row' : ''
          }
          onChange={handleTableChange}
          pagination={{
            current: (parseInt(pageData.query.page) || 0) + 1,
            total: pageData.total,
            pageSize: parseInt(pageData.query.size) || 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
          }}
        >
          <Column dataIndex="userName" title="User Name" fixed="left" />
          <Column dataIndex="id" title="Id" />
          {redeemUser && <Column dataIndex="name" title="Name" />}
          {redeemUser && <Column dataIndex="phone" title="Phone" />}
          {redeemUser && <Column dataIndex="email" title="Email" />}
          <Column dataIndex="country" title="Country" />
          {redeemUser && <Column dataIndex="address" title="Address" />}
          {redeemUser && (
            <Column dataIndex="addressType" title="Address Type" />
          )}

          <Column dataIndex="createdOn" title="Created Date" />
          <Column dataIndex="modifiedOn" title="Last Update" />
          <Column dataIndex="overdueDays" title="Days Overdue" sorter={true} />
          <Column
            dataIndex="marketPlaceCategory"
            title="Category"
            render={(category: string) => formatCategory(category)}
          />
          <Column
            dataIndex="marketPlaceName"
            title="Product Name"
            render={(marketPlaceName: string) => (
              <a
                style={{ color: 'blue', cursor: 'pointer' }}
                onClick={() => handleMarketplaceClick(marketPlaceName)}
              >
                <Typography.Text style={{ color: '#0682fa' }}>
                  {marketPlaceName}
                </Typography.Text>
              </a>
            )}
          />
          <Column dataIndex="price" title="Product Value" />
          <Column dataIndex="redeemedPoints" title="Redeem Points" />
          <Column dataIndex="redeemType" title="Redeem Type" />
          <Column
            title="User Validation"
            dataIndex="userValidationStatus"
            width={170}
            render={(state: string, record: any) => {
              const value = state || 'UNDER_REVIEW'
              return (
                <Select
                  value={value}
                  onChange={(val) => handleValidationStateChange(val, record)}
                  style={{ width: '100%' }}
                >
                  {REDEEM_USER_VALIDATION_STATES.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              )
            }}
          />
          <Column
            title="Delay Status"
            dataIndex="delayStatus"
            width={120}
            render={(delayStatus: string | null, record: any) => {
              const displayStatus = normalizeDelayStatus(delayStatus)

              return (
                <div
                  className={`delay-cell ${getDelayStatusClass(displayStatus)}`}
                >
                  <Select
                    value={displayStatus}
                    placeholder="Select"
                    style={{ width: '100%' }}
                    disabled={!redeemUser}
                    onChange={(value) => handleDelayStatusChange(value, record)}
                  >
                    {DELAY_STATUS_OPTIONS.map((opt) => (
                      <Select.Option
                        key={opt.value}
                        value={opt.value}
                        disabled={opt.value === 'NO_DELAY'}
                      >
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )
            }}
          />

          <Column
            title="Tracking Status"
            dataIndex="state"
            width={170}
            render={(state: string, record: any) => (
              <Select
                value={state}
                onChange={(value) => handleStateChange(value, record)}
                disabled={!redeemUser}
                style={{ width: '100%' }}
              >
                {STATESOPTIONS.filter((opt) => opt.value !== '').map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
          {redeemUser && (
            <Column
              title="Action"
              render={(_, record: any) => (
                <Space>
                  <Link to={`/users/detail/${record.userName}`}>
                    <a className="link-btn">
                      <EyeOutlined />
                    </a>
                  </Link>
                </Space>
              )}
            />
          )}
        </Table>
      </DndProvider>
      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        title={marketplaceDetails?.name || 'Marketplace Details'}
        centered
        width={600}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : marketplaceDetails ? (
          <div style={{ textAlign: 'center' }}>
            {carouselImages.length > 0 ? (
              <Carousel
                autoplay
                dots
                style={{ maxWidth: 400, margin: '0 auto' }}
              >
                {carouselImages.map((img, index) => (
                  <div key={index}>
                    <Image
                      src={img}
                      alt={`marketplace-${index}`}
                      style={{
                        maxHeight: '300px',
                        objectFit: 'contain',
                        margin: '0 auto',
                        borderRadius: 8,
                      }}
                    />
                  </div>
                ))}
              </Carousel>
            ) : (
              <p>No images available</p>
            )}
            <p style={{ marginTop: 16 }}>{marketplaceDetails?.description}</p>
          </div>
        ) : (
          <p>This marketplace has been deleted.</p>
        )}
      </Modal>

      <Modal
        visible={confirmVisible}
        title="Confirm Cancellation"
        okText="Yes, Cancel"
        cancelText="No"
        onCancel={() => {
          setConfirmVisible(false)
          setPendingStateChange(null)
        }}
        onOk={() => {
          if (pendingStateChange) {
            applyStateChange(
              pendingStateChange.value,
              pendingStateChange.record
            )
          }
          setConfirmVisible(false)
          setPendingStateChange(null)
        }}
      >
        <Typography.Text>
          Are you sure you want to cancel this redemption? This action cannot be
          undone.
        </Typography.Text>
      </Modal>
      <Modal
        visible={emailConfirmVisible}
        title="Send Email Notification"
        footer={[
          <Button
            key="no"
            onClick={() => {
              if (pendingEmailStateChange) {
                applyStateChange(
                  pendingEmailStateChange.value,
                  pendingEmailStateChange.record,
                  false
                )
              }
              setEmailConfirmVisible(false)
              setPendingEmailStateChange(null)
            }}
          >
            No, Don't Send
          </Button>,

          <Button
            key="yes"
            type="primary"
            onClick={() => {
              if (pendingEmailStateChange) {
                applyStateChange(
                  pendingEmailStateChange.value,
                  pendingEmailStateChange.record,
                  true
                )
              }
              setEmailConfirmVisible(false)
              setPendingEmailStateChange(null)
            }}
          >
            Yes, Send Email
          </Button>,
        ]}
        onCancel={() => {
          setEmailConfirmVisible(false)
          setPendingEmailStateChange(null)
        }}
      >
        <Typography.Text>Do you want to send an email ?</Typography.Text>
      </Modal>
      <Modal
        visible={delayModalVisible}
        title={`Delay Reason - ${selectedDelay?.status}`}
        okText="Save"
        onOk={handleSaveDelayStatus}
        onCancel={() => setDelayModalVisible(false)}
      >
        <Typography.Text>Reason</Typography.Text>
        <Input.TextArea
          rows={4}
          style={{ width: '100%', marginTop: 8 }}
          value={delayReason}
          onChange={(e) => setDelayReason(e.target.value)}
          placeholder="Enter reason for delay"
        />
      </Modal>
      <Modal
        visible={bulkEmailConfirmVisible}
        title="Send Email Notification (Bulk Update)"
        footer={[
          <Button
            key="no"
            onClick={() => {
              executeBulkUpdate(false)
              setBulkEmailConfirmVisible(false)
              setPendingBulkEmailAction(null)
            }}
          >
            No, Don't Send
          </Button>,

          <Button
            key="yes"
            type="primary"
            onClick={() => {
              executeBulkUpdate(true)
              setBulkEmailConfirmVisible(false)
              setPendingBulkEmailAction(null)
            }}
          >
            Yes, Send Email
          </Button>,
        ]}
        onCancel={() => {
          setBulkEmailConfirmVisible(false)
          setPendingBulkEmailAction(null)
        }}
      >
        <Typography.Text>
          Do you want to send email notifications for all selected records?
        </Typography.Text>
      </Modal>
    </>
  )
}

export default RedeemUsers
